// src/pages/CaseDetailPage.tsx
import React, { useState } from "react";
import { Link } from "../../design/link";
import { ChevronLeftIcon } from "@heroicons/react/16/solid";
import { Heading, Subheading } from "../../design/heading";
import {
  DescriptionDetails,
  DescriptionList,
  DescriptionTerm,
} from "../../design/description-list";
import { Divider } from "../../design/divider";
import CaseDetailFileList from "./components/FileList/CaseDetailFilesList";
import CaseDetailEventList from "./components/Visits/CaseDetailVisitsList";
import CaseDetailLocationList from "./components/Locations/CaseDetailLocationList";
import CaseDetailUserList from "./components/Parents/CaseDetailUserList";
import CaseDetailChildList from "./components/Children/CaseDetailChildList";
import CaseDetailAttorneyList from "./components/Attorneys/CaseDetailAttorneyList";
import CaseManagementTab from "./components/CaseManagement/CaseManagementTab";
import AssessmentsTab from "./components/Assessments/AssessmentsTab";
import GoalsTab from "./components/Goals/GoalsTab";
import CaseDetailReportsList from "./components/Reports/CaseDetailReportsList";
import CaseDetailPhoneCallList from "./components/PhoneCalls/CaseDetailPhoneCallList";
import { CaseManagementProvider } from "../../contexts/CaseManagementContext";
import LoadingView from "../../components/LoadingView";
import EditCaseDialog from "./components/EditCaseDialog";
import CaseDetailContractList from "./components/Contracts/CaseDetailContractsList";
import CaseStatusBadge from "../../components/CaseStatusBadge";
import EditCourtAddressButton from "./components/EditCourtAddressButton";
import { Text } from "../../design/text";
import {
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownLabel,
  DropdownMenu,
} from "../../design/dropdown";
import {
  EllipsisHorizontalIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  UsersIcon,
  MapPinIcon,
  DocumentTextIcon,
  FolderIcon,
  ScaleIcon,
  ClipboardDocumentListIcon,
  ExclamationTriangleIcon,
  DocumentDuplicateIcon,
  ChartBarIcon,
  FlagIcon,
  PhoneIcon,
} from "@heroicons/react/20/solid";
import CreateFL324PDialog from "../../components/Dialogs/CreateFL324PDialog";
import { Badge } from "../../design/badge";
import ErrorView from "../../components/ErrorView";
import DeleteCaseDialog from "../../components/DeleteCaseDialog";
import { useCaseDetail } from "./hooks/useCaseDetail";
import { useCase } from "../../contexts/CaseContext";
import { useNavigate } from "react-router-dom";
import { Case } from "@visitproof/types";
import {
  REASON_LABELS,
  ReasonForSupervision,
} from "@visitproof/types";
import { Navbar, NavbarItem } from "../../design/navbar";
import { Button } from "../../design/button";
import { formatDateUTC } from "../../utils/date";

type TabType = "people" | "attorneys" | "locations" | "documents" | "visits" | "reports" | "phone_calls" | "assessments" | "goals" | "case_management";

// Stats Card Component
interface StatCardProps {
  label: string;
  value: number;
  icon: React.ReactNode;
  color: "blue" | "green" | "red" | "amber";
}

const StatCard: React.FC<StatCardProps> = ({ label, value, icon, color }) => {
  const colorClasses = {
    blue: "bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-800",
    green: "bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 border-green-200 dark:border-green-800",
    red: "bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 border-red-200 dark:border-red-800",
    amber: "bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 border-amber-200 dark:border-amber-800",
  };

  return (
    <div className={`flex items-center gap-3 p-4 rounded-xl border ${colorClasses[color]}`}>
      <div className="flex-shrink-0">{icon}</div>
      <div>
        <p className="text-2xl font-bold">{value}</p>
        <p className="text-sm opacity-80">{label}</p>
      </div>
    </div>
  );
};

// Closure Banner - shown when case is closed
interface ClosureBannerProps {
  caseItem: Case;
}

const ClosureBanner: React.FC<ClosureBannerProps> = React.memo(({ caseItem }) => {
  if (caseItem.status !== "closed") return null;

  return (
    <div className="p-4 border rounded-xl bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800">
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0">
          <ExclamationTriangleIcon className="w-5 h-5 text-amber-600 dark:text-amber-400" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-medium text-amber-800 dark:text-amber-200">
            Case Closed
            {caseItem.closedAt && (
              <span className="ml-2 font-normal text-amber-600 dark:text-amber-400">
                on {formatDateUTC(caseItem.closedAt, "long")}
              </span>
            )}
          </h3>
          {caseItem.closureNotes && (
            <p className="mt-1 text-sm text-amber-700 dark:text-amber-300 whitespace-pre-wrap">
              {caseItem.closureNotes}
            </p>
          )}
        </div>
      </div>
    </div>
  );
});

// Header with Breadcrumb
const Breadcrumb: React.FC = () => (
  <div className="max-lg:hidden">
    <Link
      href="/cases"
      className="inline-flex items-center gap-2 transition-colors text-sm/6 text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-300"
    >
      <ChevronLeftIcon className="size-4 fill-zinc-400 dark:fill-zinc-500" />
      Cases
    </Link>
  </div>
);

// Case Header with Status and Actions
interface CaseHeaderProps {
  caseItem: Case;
}

const CaseHeader: React.FC<CaseHeaderProps> = React.memo(({ caseItem }) => {
  const { deleteCaseById } = useCase();
  const navigate = useNavigate();
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [createFL324PDialogOpen, setCreateFL324PDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const handleDeleteConfirm = async () => {
    await deleteCaseById(caseItem.id);
    navigate("/cases");
  };

  return (
    <div className="mt-4 lg:mt-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <Heading>Case {caseItem.identifier}</Heading>
          <CaseStatusBadge status={caseItem.status} />
        </div>
        <div className="flex items-center gap-2">
          <Button outline onClick={() => setEditDialogOpen(true)}>
            Edit Case
          </Button>
          <EditCaseDialog
            caseItem={caseItem}
            isOpen={editDialogOpen}
            setIsOpen={setEditDialogOpen}
          />
          <CreateFL324PDialog
            caseItem={caseItem}
            isOpen={createFL324PDialogOpen}
            setIsOpen={setCreateFL324PDialogOpen}
          />
          <DeleteCaseDialog
            caseItem={caseItem}
            isOpen={deleteDialogOpen}
            onClose={() => setDeleteDialogOpen(false)}
            onConfirm={handleDeleteConfirm}
          />
          <Dropdown>
            <DropdownButton outline>
              <EllipsisHorizontalIcon className="w-5 h-5" />
            </DropdownButton>
            <DropdownMenu className="min-w-64" anchor={"bottom end"}>
              <DropdownItem onClick={() => setCreateFL324PDialogOpen(true)}>
                <DropdownLabel>Create FL-324P</DropdownLabel>
              </DropdownItem>
              <DropdownItem onClick={() => setDeleteDialogOpen(true)}>
                <DropdownLabel className="text-red-500">Delete Case</DropdownLabel>
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
        </div>
      </div>
    </div>
  );
});

// Visit Stats Cards
interface VisitStatsProps {
  caseItem: Case;
}

const VisitStats: React.FC<VisitStatsProps> = React.memo(({ caseItem }) => (
  <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
    <StatCard
      label="Scheduled"
      value={caseItem.numberOfScheduledVisits ?? 0}
      icon={<ClockIcon className="w-6 h-6" />}
      color="blue"
    />
    <StatCard
      label="Completed"
      value={caseItem.numberOfCompletedVisits ?? 0}
      icon={<CheckCircleIcon className="w-6 h-6" />}
      color="green"
    />
    <StatCard
      label="Canceled"
      value={caseItem.numberOfCanceledVisits ?? 0}
      icon={<XCircleIcon className="w-6 h-6" />}
      color="red"
    />
    <StatCard
      label="Total Visits"
      value={
        (caseItem.numberOfScheduledVisits ?? 0) +
        (caseItem.numberOfCompletedVisits ?? 0) +
        (caseItem.numberOfCanceledVisits ?? 0)
      }
      icon={<CalendarDaysIcon className="w-6 h-6" />}
      color="amber"
    />
  </div>
));

// Case Details Card
interface CaseDetailsCardProps {
  caseItem: Case;
  inquiryDate: string | null;
}

const CaseDetailsCard: React.FC<CaseDetailsCardProps> = React.memo(
  ({ caseItem, inquiryDate }) => {
    const courtAddress = caseItem.courtAddress;
    const addressParts = courtAddress
      ? [
          courtAddress.county,
          courtAddress.streetAddress,
          courtAddress.mailingAddress,
          courtAddress.city,
          courtAddress.zipCode,
          courtAddress.branchName,
        ].filter(Boolean)
      : [];

    return (
      <div className="p-6 bg-white border shadow-sm rounded-xl dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700">
        <Subheading>Case Information</Subheading>
        <Divider className="my-4" />
        <DescriptionList>
          <DescriptionTerm>Identifier</DescriptionTerm>
          <DescriptionDetails>{caseItem.identifier}</DescriptionDetails>

          {caseItem.inquiryId && inquiryDate && (
            <>
              <DescriptionTerm>Inquiry</DescriptionTerm>
              <DescriptionDetails>
                <Link
                  href={`/inquiry/${caseItem.inquiryId}`}
                  className="text-blue-600 dark:text-blue-400 hover:underline"
                >
                  {inquiryDate}
                </Link>
              </DescriptionDetails>
            </>
          )}

          {caseItem.notes && (
            <>
              <DescriptionTerm>Notes</DescriptionTerm>
              <DescriptionDetails className="whitespace-pre-wrap">
                {caseItem.notes}
              </DescriptionDetails>
            </>
          )}

          {addressParts.length > 0 && (
            <>
              <DescriptionTerm>Court Address</DescriptionTerm>
              <DescriptionDetails>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex flex-col gap-0.5">
                    {addressParts.map((part, index) => (
                      <Text key={`address-part-${index}-${part}`}>{part}</Text>
                    ))}
                  </div>
                  <EditCourtAddressButton caseItem={caseItem} />
                </div>
              </DescriptionDetails>
            </>
          )}
        </DescriptionList>

        {addressParts.length === 0 && (
          <div className="flex items-center justify-between pt-4 mt-4 border-t border-zinc-100 dark:border-zinc-800">
            <div>
              <p className="text-sm font-medium text-zinc-700 dark:text-zinc-300">Court Address</p>
              <p className="text-sm text-zinc-500 dark:text-zinc-400">No court address provided</p>
            </div>
            <EditCourtAddressButton caseItem={caseItem} />
          </div>
        )}
      </div>
    );
  }
);

// Demographics Card
interface DemographicsCardProps {
  caseItem: Case;
}

const DemographicsCard: React.FC<DemographicsCardProps> = React.memo(({ caseItem }) => {
  const hasDemographics =
    caseItem.reasonForSupervision || caseItem.referralSource || caseItem.referralDate || caseItem.intakeDate;

  return (
    <div className="p-6 bg-white border shadow-sm rounded-xl dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700">
      <Subheading>Demographics</Subheading>
      <Divider className="my-4" />
      {!hasDemographics ? (
        <p className="text-sm text-zinc-500 dark:text-zinc-400">
          No demographics information recorded. Edit the case to add demographics for grant
          reporting.
        </p>
      ) : (
        <DescriptionList>
          {caseItem.intakeDate && (
            <>
              <DescriptionTerm>Intake Date</DescriptionTerm>
              <DescriptionDetails>
                <Badge color="blue">
                  {formatDateUTC(caseItem.intakeDate, "long")}
                </Badge>
              </DescriptionDetails>
            </>
          )}

          {caseItem.reasonForSupervision && (
            <>
              <DescriptionTerm>Reason for Supervision</DescriptionTerm>
              <DescriptionDetails>
                <Badge color="amber">
                  {caseItem.reasonForSupervision === "other" && caseItem.reasonForSupervisionOther
                    ? caseItem.reasonForSupervisionOther
                    : REASON_LABELS[caseItem.reasonForSupervision as ReasonForSupervision] ||
                      caseItem.reasonForSupervision}
                </Badge>
              </DescriptionDetails>
            </>
          )}

          {caseItem.referralSource && (
            <>
              <DescriptionTerm>Referral Source</DescriptionTerm>
              <DescriptionDetails>{caseItem.referralSource}</DescriptionDetails>
            </>
          )}

          {caseItem.referralDate && (
            <>
              <DescriptionTerm>Referral Date</DescriptionTerm>
              <DescriptionDetails>
                {formatDateUTC(caseItem.referralDate, "long")}
              </DescriptionDetails>
            </>
          )}
        </DescriptionList>
      )}
    </div>
  );
});

// Tab Navigation
interface TabNavigationProps {
  activeTab: TabType;
  onTabChange: (tab: TabType) => void;
}

const TabNavigation: React.FC<TabNavigationProps> = ({ activeTab, onTabChange }) => (
  <div className="border-b border-zinc-200 dark:border-zinc-700">
    <Navbar className="-mb-px">
      <NavbarItem current={activeTab === "people"} onClick={() => onTabChange("people")}>
        <UsersIcon className="w-5 h-5" data-slot="icon" />
        People
      </NavbarItem>
      <NavbarItem current={activeTab === "attorneys"} onClick={() => onTabChange("attorneys")}>
        <ScaleIcon className="w-5 h-5" data-slot="icon" />
        Attorneys
      </NavbarItem>
      <NavbarItem current={activeTab === "locations"} onClick={() => onTabChange("locations")}>
        <MapPinIcon className="w-5 h-5" data-slot="icon" />
        Locations
      </NavbarItem>
      <NavbarItem current={activeTab === "documents"} onClick={() => onTabChange("documents")}>
        <DocumentTextIcon className="w-5 h-5" data-slot="icon" />
        Documents
      </NavbarItem>
      <NavbarItem current={activeTab === "visits"} onClick={() => onTabChange("visits")}>
        <FolderIcon className="w-5 h-5" data-slot="icon" />
        Visits
      </NavbarItem>
      <NavbarItem current={activeTab === "reports"} onClick={() => onTabChange("reports")}>
        <DocumentDuplicateIcon className="w-5 h-5" data-slot="icon" />
        Reports
      </NavbarItem>
      <NavbarItem current={activeTab === "phone_calls"} onClick={() => onTabChange("phone_calls")}>
        <PhoneIcon className="w-5 h-5" data-slot="icon" />
        Phone Calls
      </NavbarItem>
      <NavbarItem current={activeTab === "assessments"} onClick={() => onTabChange("assessments")}>
        <ChartBarIcon className="w-5 h-5" data-slot="icon" />
        Assessments
      </NavbarItem>
      <NavbarItem current={activeTab === "goals"} onClick={() => onTabChange("goals")}>
        <FlagIcon className="w-5 h-5" data-slot="icon" />
        Goals
      </NavbarItem>
      <NavbarItem current={activeTab === "case_management"} onClick={() => onTabChange("case_management")}>
        <ClipboardDocumentListIcon className="w-5 h-5" data-slot="icon" />
        Case Management
      </NavbarItem>
    </Navbar>
  </div>
);

// Tab Content Components
interface TabContentProps {
  caseId: string;
}

const PeopleTab: React.FC<TabContentProps> = ({ caseId }) => (
  <div className="space-y-6">
    <div className="p-6 bg-white border shadow-sm rounded-xl dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700">
      <CaseDetailUserList caseId={caseId} />
    </div>
    <div className="p-6 bg-white border shadow-sm rounded-xl dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700">
      <CaseDetailChildList caseId={caseId} />
    </div>
  </div>
);

const AttorneysTab: React.FC<TabContentProps> = ({ caseId }) => (
  <div className="p-6 bg-white border shadow-sm rounded-xl dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700">
    <CaseDetailAttorneyList caseId={caseId} />
  </div>
);

const LocationsTab: React.FC<TabContentProps> = ({ caseId }) => (
  <div className="p-6 bg-white border shadow-sm rounded-xl dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700">
    <CaseDetailLocationList caseId={caseId} />
  </div>
);

const DocumentsTab: React.FC<TabContentProps> = ({ caseId }) => (
  <div className="space-y-6">
    <div className="p-6 bg-white border shadow-sm rounded-xl dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700">
      <CaseDetailContractList caseId={caseId} />
    </div>
    <div className="p-6 bg-white border shadow-sm rounded-xl dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700">
      <CaseDetailFileList caseId={caseId} />
    </div>
  </div>
);

const VisitsTab: React.FC<TabContentProps> = ({ caseId }) => (
  <div className="p-6 bg-white border shadow-sm rounded-xl dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700">
    <CaseDetailEventList caseId={caseId} />
  </div>
);

const ReportsTab: React.FC<TabContentProps> = ({ caseId }) => (
  <div className="p-6 bg-white border shadow-sm rounded-xl dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700">
    <CaseDetailReportsList caseId={caseId} />
  </div>
);

const PhoneCallsTab: React.FC<TabContentProps> = ({ caseId }) => (
  <div className="p-6 bg-white border shadow-sm rounded-xl dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700">
    <CaseDetailPhoneCallList caseId={caseId} />
  </div>
);

const CaseManagementTabContent: React.FC<TabContentProps> = ({ caseId }) => (
  <CaseManagementProvider>
    <div className="p-6 bg-white border shadow-sm rounded-xl dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700">
      <CaseManagementTab caseId={caseId} />
    </div>
  </CaseManagementProvider>
);

// Main Component
const CaseDetailPage: React.FC = () => {
  const { selectedCase, loading, error, inquiryDate } = useCaseDetail();
  const [activeTab, setActiveTab] = useState<TabType>("people");

  if (loading.details) {
    return <LoadingView />;
  }

  if (error.details) {
    return <ErrorView error={error.details} />;
  }

  if (!selectedCase || !selectedCase.id) {
    return <ErrorView error={new Error("Case not found")} />;
  }

  const caseId = selectedCase.id;

  return (
    <div className="space-y-6">
      {/* Breadcrumb Navigation */}
      <Breadcrumb />

      {/* Case Header with Status and Actions */}
      <CaseHeader caseItem={selectedCase} />

      {/* Closure Banner - shown when case is closed */}
      <ClosureBanner caseItem={selectedCase} />

      {/* Visit Statistics */}
      <VisitStats caseItem={selectedCase} />

      {/* Case Details and Demographics - Two Column Layout on Desktop */}
      <div className="grid gap-6 lg:grid-cols-2">
        <CaseDetailsCard caseItem={selectedCase} inquiryDate={inquiryDate} />
        <DemographicsCard caseItem={selectedCase} />
      </div>

      {/* Tabbed Related Data */}
      <div className="space-y-6">
        <TabNavigation activeTab={activeTab} onTabChange={setActiveTab} />

        {/* Tab Content */}
        <div>
          {activeTab === "people" && <PeopleTab caseId={caseId} />}
          {activeTab === "attorneys" && <AttorneysTab caseId={caseId} />}
          {activeTab === "locations" && <LocationsTab caseId={caseId} />}
          {activeTab === "documents" && <DocumentsTab caseId={caseId} />}
          {activeTab === "visits" && <VisitsTab caseId={caseId} />}
          {activeTab === "reports" && <ReportsTab caseId={caseId} />}
          {activeTab === "phone_calls" && <PhoneCallsTab caseId={caseId} />}
          {activeTab === "assessments" && (
            <div className="p-6 bg-white border shadow-sm rounded-xl dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700">
              <AssessmentsTab caseId={caseId} />
            </div>
          )}
          {activeTab === "goals" && (
            <div className="p-6 bg-white border shadow-sm rounded-xl dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700">
              <GoalsTab caseId={caseId} />
            </div>
          )}
          {activeTab === "case_management" && <CaseManagementTabContent caseId={caseId} />}
        </div>
      </div>
    </div>
  );
};

export default CaseDetailPage;
